import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import './ArrangeResultsTabs.css';

/**
 * Brand Admin and up, from the Results page: the strategy tab order, who sees
 * each tab (Public / Internal / Hidden), each strategy's contract or symbol,
 * and old tab names that now point at another tab (so saved strat combos that
 * still use an old name keep working). Replaces the old Sheet Manager page.
 *
 * Fires SHEETS_CHANGED_EVENT after a save so App reloads the tab list.
 */
export const SHEETS_CHANGED_EVENT = 'risklo:sheets-changed';
/** Set before going to Results to open this panel there (old Sheet Manager links). */
export const OPEN_ARRANGE_KEY = 'risklo.results.openArrangeTabs';

const VISIBILITY = [
  { value: 'public', label: 'Public' },
  { value: 'internal', label: 'Internal (admins)' },
  { value: 'hidden', label: 'Hidden' },
];
const CLASSES = [
  { value: 'futures', label: 'Futures' },
  { value: 'stock', label: 'Stock / ETF' },
  { value: 'crypto', label: 'Crypto' },
];

function headers() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

async function readJson(res, fallback) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || fallback);
  return data;
}

/** Rows from /api/admin/sheets → the editable list (live strategy tabs only). */
export function toArrangeRows(sheets) {
  return (Array.isArray(sheets) ? sheets : [])
    .filter((s) => s && s.live !== false)
    .map((s) => ({
      name: s.sheet_name,
      label: s.displayLabel || s.displayName || s.sheet_name,
      isMasterStrat: Boolean(s.isMasterStrat),
      visibility: ['public', 'internal', 'hidden'].includes(s.visibility) ? s.visibility : 'public',
      instrumentClass: s.instrumentMeta?.instrumentClass || 'futures',
      displaySymbol: s.instrumentMeta?.displaySymbol || '',
      savedClass: s.instrumentMeta?.instrumentClass || 'futures',
      savedSymbol: s.instrumentMeta?.displaySymbol || '',
    }));
}

/**
 * Old names that point at another tab, from /api/admin/sheets. `live` is an
 * old name that's still a tab of its own; otherwise only the mapping is left.
 */
export function toMappings(sheets) {
  return (Array.isArray(sheets) ? sheets : [])
    .filter((s) => s && s.alias_for && !s.isMasterStrat)
    .map((s) => ({ name: s.sheet_name, target: s.alias_for, live: s.live !== false }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Move one row from `from` to `to`, returning a new array. */
export function moveRow(rows, from, to) {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return rows;
  const next = [...rows];
  const [picked] = next.splice(from, 1);
  next.splice(to, 0, picked);
  return next;
}

export default function ArrangeResultsTabs({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [savedOrder, setSavedOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [newOld, setNewOld] = useState('');
  const [newTarget, setNewTarget] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await readJson(await fetch(API_ENDPOINTS.adminSheets, { headers: headers() }), 'Could not load the strategy tabs.');
      const next = toArrangeRows(data.sheets);
      setRows(next);
      setMappings(toMappings(data.sheets));
      setSavedOrder(next.map((r) => r.name));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const changed = () => window.dispatchEvent(new CustomEvent(SHEETS_CHANGED_EVENT));
  const orderDirty = rows.map((r) => r.name).join('\n') !== savedOrder.join('\n');
  const patch = (name, p) => setRows((prev) => prev.map((r) => (r.name === name ? { ...r, ...p } : r)));

  const saveOrder = async () => {
    setBusy('order');
    setError(null);
    setMessage(null);
    try {
      const order = rows.map((r) => r.name);
      await readJson(await fetch(API_ENDPOINTS.adminSheetsReorder, {
        method: 'POST', headers: headers(), body: JSON.stringify({ order }),
      }), 'Could not save the order.');
      setSavedOrder(order);
      setMessage('Order saved. Everyone sees the new order.');
      changed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const saveVisibility = async (row, visibility) => {
    const before = row.visibility;
    patch(row.name, { visibility });
    setBusy(`vis:${row.name}`);
    setError(null);
    setMessage(null);
    try {
      await readJson(await fetch(API_ENDPOINTS.adminSheetsVisibility, {
        method: 'POST', headers: headers(), body: JSON.stringify({ name: row.name, visibility }),
      }), 'Could not change who sees this tab.');
      setMessage(visibility === 'hidden'
        ? `${row.label} is hidden from Results, the summary and the simulator.`
        : `${row.label} is now ${visibility === 'internal' ? 'internal (admins only)' : 'public'}.`);
      changed();
    } catch (e) {
      patch(row.name, { visibility: before });
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const saveInstrument = async (row) => {
    const symbol = row.displaySymbol.trim().toUpperCase();
    if (!symbol) { setError(`Enter a symbol for ${row.label} (e.g. NQ, MNQ, ES, AAPL).`); return; }
    setBusy(`inst:${row.name}`);
    setError(null);
    setMessage(null);
    try {
      const data = await readJson(await fetch(API_ENDPOINTS.adminScenarioInstrument(row.name), {
        method: 'PUT', headers: headers(), body: JSON.stringify({ instrumentClass: row.instrumentClass, displaySymbol: symbol }),
      }), 'Could not save the contract.');
      const saved = data.instrument || {};
      patch(row.name, {
        instrumentClass: saved.instrumentClass || row.instrumentClass,
        displaySymbol: saved.displaySymbol || symbol,
        savedClass: saved.instrumentClass || row.instrumentClass,
        savedSymbol: saved.displaySymbol || symbol,
      });
      setMessage(`${row.label}: ${saved.displaySymbol || symbol} saved.`);
      changed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const tabLabel = (name) => rows.find((r) => r.name.toLowerCase() === String(name || '').toLowerCase())?.label || name;

  const saveMapping = async (oldName, target) => {
    const name = String(oldName || '').trim();
    if (!name || !target) { setError('Type the old tab name and pick the tab it now points to.'); return; }
    if (name.toLowerCase() === target.toLowerCase()) { setError('A tab can’t point to itself.'); return; }
    setBusy(`map:${name}`);
    setError(null);
    setMessage(null);
    try {
      await readJson(await fetch(API_ENDPOINTS.adminSheets, {
        method: 'POST', headers: headers(), body: JSON.stringify({ sheet_name: name, alias_for: target }),
      }), 'Could not save the old name.');
      setMappings((prev) => {
        const others = prev.filter((m) => m.name.toLowerCase() !== name.toLowerCase());
        const live = rows.some((r) => r.name.toLowerCase() === name.toLowerCase());
        return [...others, { name, target, live }].sort((a, b) => a.name.localeCompare(b.name));
      });
      setMessage(`“${name}” now points to ${tabLabel(target)}. Saved combos using the old name show its results.`);
      setNewOld('');
      setNewTarget('');
      changed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // An old name that's still a tab keeps its order and visibility: only the
  // pointer is cleared. One that's only a mapping is removed entirely.
  const removeMapping = async (m) => {
    setBusy(`map:${m.name}`);
    setError(null);
    setMessage(null);
    try {
      const res = m.live
        ? await fetch(API_ENDPOINTS.adminSheets, {
          method: 'POST', headers: headers(), body: JSON.stringify({ sheet_name: m.name, alias_for: null }),
        })
        : await fetch(API_ENDPOINTS.adminSheetByName(m.name), { method: 'DELETE', headers: headers() });
      await readJson(res, 'Could not remove the old name.');
      setMappings((prev) => prev.filter((x) => x.name !== m.name));
      setMessage(`“${m.name}” no longer points anywhere.`);
      changed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="art-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="art-panel" role="dialog" aria-modal="true" aria-labelledby="art-title">
        <header className="art-head">
          <div>
            <h2 id="art-title">Arrange strategy tabs</h2>
            <p>Drag or use the arrows to set the tab order, then Save order. Who sees a tab and its contract save right away. This changes Results for everyone on this brand.</p>
          </div>
          <button type="button" className="art-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {error ? <div className="art-error" role="alert">{error}</div> : null}
        {message ? <div className="art-message" role="status">{message}</div> : null}

        <div className="art-body">
        {loading ? <p className="art-muted art-loading">Loading tabs…</p> : (
          <ol className="art-list">
            {rows.map((row, i) => {
              const instDirty = row.instrumentClass !== row.savedClass
                || row.displaySymbol.trim().toUpperCase() !== String(row.savedSymbol || '').toUpperCase();
              return (
                <li
                  key={row.name}
                  className={`art-row${dragIndex === i ? ' is-dragging' : ''}${row.visibility === 'hidden' ? ' is-hidden' : ''}`}
                  draggable
                  onDragStart={(e) => { setDragIndex(i); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); if (dragIndex !== null && dragIndex !== i) { setRows((prev) => moveRow(prev, dragIndex, i)); setDragIndex(i); } }}
                  onDragEnd={() => setDragIndex(null)}
                >
                  <span className="art-grip" aria-hidden="true">⠿</span>
                  <span className="art-pos">{i + 1}</span>
                  <div className="art-name">
                    <strong>{row.label}</strong>
                    {row.label !== row.name ? <small>{row.name}</small> : null}
                  </div>
                  <div className="art-move">
                    <button type="button" onClick={() => setRows((p) => moveRow(p, i, 0))} disabled={i === 0} title="Move to top" aria-label={`Move ${row.label} to top`}>⤒</button>
                    <button type="button" onClick={() => setRows((p) => moveRow(p, i, i - 1))} disabled={i === 0} aria-label={`Move ${row.label} up`}>↑</button>
                    <button type="button" onClick={() => setRows((p) => moveRow(p, i, i + 1))} disabled={i === rows.length - 1} aria-label={`Move ${row.label} down`}>↓</button>
                  </div>
                  <label className="art-field">
                    <span>Who sees it</span>
                    <select
                      value={row.visibility}
                      onChange={(e) => saveVisibility(row, e.target.value)}
                      disabled={busy === `vis:${row.name}`}
                    >
                      {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </label>
                  {row.isMasterStrat ? <span className="art-muted art-inst">Combines other tabs</span> : (
                    <div className="art-inst">
                      <label className="art-field">
                        <span>Type</span>
                        <select value={row.instrumentClass} onChange={(e) => patch(row.name, { instrumentClass: e.target.value })}>
                          {CLASSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </label>
                      <label className="art-field art-field--symbol">
                        <span>Contract / symbol</span>
                        <input
                          value={row.displaySymbol}
                          placeholder="NQ"
                          maxLength={16}
                          onChange={(e) => patch(row.name, { displaySymbol: e.target.value.toUpperCase() })}
                          onKeyDown={(e) => { if (e.key === 'Enter' && instDirty) saveInstrument(row); }}
                        />
                      </label>
                      <button
                        type="button"
                        className="art-btn"
                        onClick={() => saveInstrument(row)}
                        disabled={!instDirty || busy === `inst:${row.name}`}
                      >
                        {busy === `inst:${row.name}` ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
            {!rows.length ? <li className="art-muted">No strategy tabs on this brand yet.</li> : null}
          </ol>
        )}

        {!loading ? (
          <section className="art-maps" aria-labelledby="art-maps-title">
            <h3 id="art-maps-title">Renamed or removed tabs</h3>
            <p className="art-muted">
              When a strategy tab is renamed or deleted, point its old name at the tab that replaced it.
              Saved strat combos that still use the old name then show the new tab’s results.
            </p>
            {mappings.length ? (
              <ul className="art-map-list">
                {mappings.map((m) => (
                  <li key={m.name} className="art-map">
                    <span className="art-map-old" title={m.live ? 'Still a tab of its own too' : 'No longer a tab'}>{m.name}</span>
                    <span className="art-map-arrow" aria-hidden="true">→</span>
                    <select
                      value={m.target}
                      onChange={(e) => saveMapping(m.name, e.target.value)}
                      disabled={busy === `map:${m.name}`}
                      aria-label={`What ${m.name} points to`}
                    >
                      {rows.some((r) => r.name.toLowerCase() === m.target.toLowerCase()) ? null : <option value={m.target}>{m.target} (not a tab)</option>}
                      {rows.filter((r) => !r.isMasterStrat && r.name !== m.name).map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                    </select>
                    <button type="button" className="art-btn" onClick={() => removeMapping(m)} disabled={busy === `map:${m.name}`}>Remove</button>
                  </li>
                ))}
              </ul>
            ) : <p className="art-muted art-map-none">No old names yet.</p>}
            <div className="art-map art-map-add">
              <input
                value={newOld}
                onChange={(e) => setNewOld(e.target.value)}
                placeholder="Old tab name, exactly as it was"
                aria-label="Old tab name"
                maxLength={200}
              />
              <span className="art-map-arrow" aria-hidden="true">→</span>
              <select value={newTarget} onChange={(e) => setNewTarget(e.target.value)} aria-label="The tab it now points to">
                <option value="">Points to…</option>
                {rows.filter((r) => !r.isMasterStrat).map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
              </select>
              <button
                type="button"
                className="art-btn art-btn--primary"
                onClick={() => saveMapping(newOld, newTarget)}
                disabled={!newOld.trim() || !newTarget || busy === `map:${newOld.trim()}`}
              >
                Add
              </button>
            </div>
          </section>
        ) : null}
        </div>

        <footer className="art-foot">
          <span className="art-muted">{orderDirty ? 'Order changed, not saved yet.' : 'Current Results, Past Results and Summary always come first.'}</span>
          <div>
            {orderDirty ? (
              <button type="button" className="art-btn" onClick={() => setRows((prev) => savedOrder.map((n) => prev.find((r) => r.name === n)).filter(Boolean))}>Undo</button>
            ) : null}
            <button type="button" className="art-btn art-btn--primary" onClick={saveOrder} disabled={!orderDirty || busy === 'order'}>
              {busy === 'order' ? 'Saving…' : 'Save order'}
            </button>
            <button type="button" className="art-btn" onClick={onClose}>Done</button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
